import { forwardRef, useId, useRef, useState, type FocusEvent, type InputHTMLAttributes, type MutableRefObject, type ReactNode } from "react";
import { ChevronDown, ChevronUp } from "lucide-react";
import { normalizarTextoPtBr } from "../../utils/textoPtBr";
import { useIdioma } from "../../i18n/IdiomaProvider";
import {
  navegadorMascaraSenhaPorCss,
  obterAtributosAssistenciaTexto,
  obterAtributosSemSugestoesNativas,
  precisaBloqueioAteFoco,
} from "./assistenciaTexto";
import {
  campoEstaPreenchido,
  inferirTamanhoCampoModal,
  type TamanhoCampoModal,
} from "./campoModal";
import { RotuloObrigatorio } from "./rotuloObrigatorio";
import styles from "./CampoTexto.module.css";

export interface CampoTextoProps extends InputHTMLAttributes<HTMLInputElement> {
  /**
   * Bloqueado de propósito. O autocomplete de todo campo da plataforma é
   * decidido só em `obterAtributosSemSugestoesNativas`, para nenhuma tela
   * conseguir reativar o autopreenchimento do navegador.
   */
  autoComplete?: never;
  /** Tooltip nativo é proibido nos campos da plataforma. */
  title?: never;
  rotulo?: string;
  erro?: string;
  iconeInicio?: ReactNode;
  iconeFim?: ReactNode;
  acaoFim?: ReactNode;
  controleClassName?: string;
  inputClassName?: string;
  rotuloClassName?: string;
  textoAuxiliar?: string;
  valido?: boolean;
  exibirIndicadorValido?: boolean;
  tamanhoCompacto?: TamanhoCampoModal;
  ignorarProgresso?: boolean;
}

/**
 * Campo de texto base (rótulo + input estilizado + mensagem de erro).
 * Serve de fundação para os campos especializados (CampoEmail, CampoCPF,
 * CampoTelefone, etc.). Respeita os temas claro/escuro.
 */
export const CampoTexto = forwardRef<HTMLInputElement, CampoTextoProps>(
  function CampoTexto(
    {
      rotulo,
      erro,
      iconeInicio,
      iconeFim,
      acaoFim,
      id,
      className,
      controleClassName,
      inputClassName,
      rotuloClassName,
      textoAuxiliar,
      valido,
      exibirIndicadorValido = true,
      tamanhoCompacto,
      ignorarProgresso = false,
      placeholder,
      type = "text",
      // Desestruturado só para NUNCA chegar ao input: quem decide o autocomplete
      // é `obterAtributosSemSugestoesNativas`, para nenhuma tela conseguir
      // reativar o autopreenchimento do navegador por engano.
      autoComplete: _autoCompleteIgnorado,
      title: _titleIgnorado,
      spellCheck,
      autoCorrect,
      autoCapitalize,
      lang,
      required,
      value,
      disabled,
      readOnly,
      onFocus,
      onBlur,
      ...rest
    },
    ref
  ) {
    const { traduzir } = useIdioma();
    const gerado = useId();
    const inputId = id || gerado;
    const inputInternoRef = useRef<HTMLInputElement | null>(null);
    const dadoBanco = String((rest as Record<string, unknown>)["data-i18n-db"] ?? "") === "true";
    const [emFoco, setEmFoco] = useState(false);
    const controlaBloqueioAteFoco = readOnly === undefined && precisaBloqueioAteFoco(type);
    const readOnlyCalculado = controlaBloqueioAteFoco ? !emFoco : readOnly;
    function tratarFoco(evento: FocusEvent<HTMLInputElement>) {
      if (controlaBloqueioAteFoco) setEmFoco(true);
      onFocus?.(evento);
    }
    function tratarBlur(evento: FocusEvent<HTMLInputElement>) {
      if (controlaBloqueioAteFoco) setEmFoco(false);
      onBlur?.(evento);
    }
    const rotuloNormalizado = rotulo ? traduzir(normalizarTextoPtBr(rotulo)) : undefined;
    const erroNormalizado = erro ? traduzir(normalizarTextoPtBr(erro)) : undefined;
    const placeholderNormalizado =
      typeof placeholder === "string"
        ? dadoBanco
          ? normalizarTextoPtBr(placeholder)
          : traduzir(normalizarTextoPtBr(placeholder))
        : placeholder;
    const valorVazio = value === undefined || value === null || value === "";
    const valorPreenchido = campoEstaPreenchido(value);
    const ignorarCampoNoProgresso =
      ignorarProgresso || ((disabled || readOnly === true) && !valorPreenchido);
    const tamanhoCampoCompacto = inferirTamanhoCampoModal(
      [rotuloNormalizado, value, placeholderNormalizado],
      tamanhoCompacto
    );
    // Quando o campo esta vazio e invalido, o erro substitui o placeholder e
    // aparece dentro do proprio input (nao abaixo, nao em caixa anexada).
    const placeholderExibido = erroNormalizado && valorVazio ? erroNormalizado : placeholderNormalizado;
    const assistenciaTexto = obterAtributosAssistenciaTexto(type);
    // Campo de senha vira `type="text"` mascarado por CSS para o gerenciador de senhas do
    // navegador não reconhecer o campo (ver `navegadorMascaraSenhaPorCss`). Vale inclusive
    // no Login: sem campo de senha nativo o Chrome não trata o envio como credencial e para
    // de abrir os avisos dele (credenciais salvas e "senha encontrada em vazamento").
    const mascararSenha = type === "password" && navegadorMascaraSenhaPorCss();
    const tipoInput = mascararSenha ? "text" : type;
    // Usa o tipo realmente renderizado: assim o atributo bate com o que a rede de
    // segurança global (`bloquearTooltipsNativos`) calcula lendo o DOM.
    const atributosSemSugestoes = obterAtributosSemSugestoesNativas(tipoInput);
    const campoNumerico = tipoInput === "number";

    function atribuirInput(elemento: HTMLInputElement | null) {
      inputInternoRef.current = elemento;
      if (typeof ref === "function") ref(elemento);
      else if (ref) (ref as MutableRefObject<HTMLInputElement | null>).current = elemento;
    }

    function ajustarNumero(direcao: 1 | -1) {
      const input = inputInternoRef.current;
      if (!input || disabled || readOnlyCalculado) return;

      const valorAnterior = input.value;
      try {
        if (direcao > 0) input.stepUp();
        else input.stepDown();
      } catch {
        return;
      }
      const proximoValor = input.value;
      if (proximoValor === valorAnterior) return;

      // Mantém o input controlado pelo React e dispara o mesmo onChange de uma
      // digitação normal, sem depender do spinner nativo de cada navegador.
      input.value = valorAnterior;
      const setterNativo = Object.getOwnPropertyDescriptor(
        window.HTMLInputElement.prototype,
        "value"
      )?.set;
      if (setterNativo) setterNativo.call(input, proximoValor);
      else input.value = proximoValor;
      input.dispatchEvent(new Event("input", { bubbles: true }));
      input.focus({ preventScroll: true });
    }

    return (
      <div
        className={`${styles.campo} ${className ?? ""}`}
        data-campo-modal="true"
        data-campo-tamanho={tamanhoCampoCompacto}
        data-campo-progresso={ignorarCampoNoProgresso ? "false" : "true"}
        data-campo-preenchido={valorPreenchido ? "true" : "false"}
        data-campo-invalido={erroNormalizado ? "true" : "false"}
      >
        {rotuloNormalizado && (
          <label htmlFor={inputId} className={[styles.rotulo, rotuloClassName].filter(Boolean).join(" ")}>
            <RotuloObrigatorio rotulo={rotuloNormalizado} obrigatorio={required} />
          </label>
        )}
        <div
          className={[
            styles.controle,
            controleClassName ?? "",
            erro ? styles.invalido : "",
            iconeInicio ? styles.comIconeInicio : "",
            iconeFim ? styles.comIconeFim : "",
            acaoFim ? styles.comAcaoFim : "",
            campoNumerico ? styles.comControlesNumero : "",
          ]
            .filter(Boolean)
            .join(" ")}
        >
          {iconeInicio && <span className={`${styles.icone} ${styles.iconeInicio}`}>{iconeInicio}</span>}
          <input
            id={inputId}
            ref={atribuirInput}
            type={tipoInput}
            value={value}
            className={[styles.input, mascararSenha ? styles.senhaMascarada : "", campoNumerico ? styles.inputNumero : "", inputClassName]
              .filter(Boolean)
              .join(" ")}
            aria-invalid={erroNormalizado ? true : undefined}
            aria-required={required || undefined}
            placeholder={placeholderExibido}
            autoComplete={atributosSemSugestoes.autoComplete}
            spellCheck={spellCheck ?? assistenciaTexto.spellCheck}
            autoCorrect={autoCorrect ?? assistenciaTexto.autoCorrect}
            autoCapitalize={autoCapitalize ?? assistenciaTexto.autoCapitalize}
            lang={lang ?? assistenciaTexto.lang}
            disabled={disabled}
            readOnly={readOnlyCalculado}
            onFocus={tratarFoco}
            onBlur={tratarBlur}
            data-form-type={atributosSemSugestoes["data-form-type"]}
            data-lpignore={atributosSemSugestoes["data-lpignore"]}
            data-1p-ignore={atributosSemSugestoes["data-1p-ignore"]}
            data-bwignore={atributosSemSugestoes["data-bwignore"]}
            {...rest}
          />
          {campoNumerico && (
            <span className={styles.controlesNumero} aria-hidden={disabled || readOnlyCalculado ? "true" : undefined}>
              <button
                type="button"
                tabIndex={-1}
                aria-label={traduzir("Aumentar valor")}
                className="tooltip"
                data-tooltip={traduzir("Aumentar valor")}
                disabled={disabled || Boolean(readOnlyCalculado)}
                onMouseDown={(evento) => evento.preventDefault()}
                onClick={() => ajustarNumero(1)}
              >
                <ChevronUp aria-hidden="true" />
              </button>
              <button
                type="button"
                tabIndex={-1}
                aria-label={traduzir("Diminuir valor")}
                className="tooltip"
                data-tooltip={traduzir("Diminuir valor")}
                disabled={disabled || Boolean(readOnlyCalculado)}
                onMouseDown={(evento) => evento.preventDefault()}
                onClick={() => ajustarNumero(-1)}
              >
                <ChevronDown aria-hidden="true" />
              </button>
            </span>
          )}
          {iconeFim && <span className={`${styles.icone} ${styles.iconeFim}`}>{iconeFim}</span>}
          {acaoFim && <span className={styles.acaoFim}>{acaoFim}</span>}
        </div>
      </div>
    );
  }
);
